import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Badge, Button, Card, CardBody, Col, Container,
  FormGroup, Input, Label, Row, Spinner, Table,
} from "reactstrap";
import {
  downloadVehicleTemplate,
  exportVehicles,
  importVehicles,
  loadVehicleImportHistory,
  previewVehicleImport,
} from "./vehicleExcelService";
import "./VehicleExcelPage.scss";

const emptyFilters = {
  dateFrom: "", dateTo: "", expert: "", lieuDepot: "", typeReforme: "",
  decision: "", immatriculation: "", referenceSinistre: "", numeroAppelOffres: "",
};

const downloadBlob = (response, fallbackName) => {
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = match?.[1] || fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const messageFrom = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.body?.message ||
  error?.message ||
  "Une erreur est survenue.";

const formatSize = (bytes) => {
  if (!bytes) return "0 Ko";
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} Mo`
    : `${Math.ceil(bytes / 1024)} Ko`;
};

const StatusPill = ({ children, tone = "neutral", icon }) => (
  <span className={`excel-status-pill excel-status-pill-${tone}`}>
    {icon && <i className={icon} />}
    {children}
  </span>
);

function VehicleExcelPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      const response = await loadVehicleImportHistory();
      setHistory(response.data?.body || []);
    } catch (error) {
      setNotice({ color: "danger", text: messageFrom(error) });
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const rejected = preview?.rejected || 0;
  const validRows = useMemo(
    () => (preview?.rows || []).filter((row) => !row.errors?.length).length,
    [preview]
  );
  const activeFilters = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters]
  );

  const runPreview = async () => {
    if (!file) {
      setNotice({ color: "warning", text: "Sélectionnez d’abord un fichier .xlsx." });
      return;
    }
    setBusy("preview");
    setNotice(null);
    try {
      const response = await previewVehicleImport(file);
      setPreview(response.data?.body);
    } catch (error) {
      setPreview(null);
      setNotice({ color: "danger", text: messageFrom(error) });
    } finally {
      setBusy("");
    }
  };

  const confirmImport = async () => {
    if (!file || !preview || validRows === 0) return;
    setBusy("import");
    try {
      const response = await importVehicles(file);
      const result = response.data?.body;
      setPreview(result);
      setNotice({
        color: "success",
        text: `Import terminé : ${result.created} créé(s), ${result.updated} mis à jour, ${result.rejected} rejeté(s).`,
      });
      await loadHistory();
    } catch (error) {
      setNotice({ color: "danger", text: messageFrom(error) });
    } finally {
      setBusy("");
    }
  };

  const downloadTemplate = async () => {
    setBusy("template");
    try {
      const response = await downloadVehicleTemplate();
      downloadBlob(response, "Modele_Etat_Vehicules_JA_Eval.xlsx");
    } catch (error) {
      setNotice({ color: "danger", text: messageFrom(error) });
    } finally {
      setBusy("");
    }
  };

  const runExport = async () => {
    setBusy("export");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      const response = await exportVehicles(params);
      const date = new Date().toISOString().slice(0, 10).replaceAll("-", "");
      downloadBlob(response, `Etat_Vehicules_JA_Eval_${date}.xlsx`);
    } catch (error) {
      setNotice({ color: "danger", text: messageFrom(error) });
    } finally {
      setBusy("");
    }
  };

  const chooseFile = (event) => {
    setFile(event.target.files?.[0] || null);
    setPreview(null);
    setNotice(null);
  };

  const setFilter = ({ target }) =>
    setFilters((current) => ({ ...current, [target.name]: target.value }));

  return (
    <div className="page-content vehicle-excel-page">
      <Container fluid className="vehicle-excel-shell">
        <section className="vehicle-excel-hero">
          <div className="vehicle-excel-hero-copy">
            <span className="vehicle-excel-eyebrow">
              <i className="ri-bar-chart-box-line" /> Rapports · États Excel
            </span>
            <h1>Import / Export des véhicules</h1>
            <p>Contrôlez, consolidez et transmettez vos états véhicules au format JA‑Eval.</p>
            <div className="vehicle-excel-hero-meta">
              <span><i className="ri-layout-column-line" /> 16 colonnes normalisées</span>
              <span><i className="ri-shield-check-line" /> Contrôle avant import</span>
              <span><i className="ri-file-excel-2-line" /> Format Excel .xlsx</span>
            </div>
          </div>
          <div className="vehicle-excel-hero-action">
            <span className="vehicle-excel-template-icon"><i className="ri-file-excel-2-line" /></span>
            <div><strong>Modèle officiel</strong><small>Prêt à compléter</small></div>
            <Button color="light" onClick={downloadTemplate} disabled={Boolean(busy)}>
              {busy === "template" ? <Spinner size="sm" /> : <i className="ri-download-cloud-2-line" />}
              Télécharger
            </Button>
          </div>
        </section>

        {notice && (
          <Alert color={notice.color} className="vehicle-excel-alert" toggle={() => setNotice(null)}>
            <i className={notice.color === "success" ? "ri-checkbox-circle-line" : "ri-information-line"} />
            <span>{notice.text}</span>
          </Alert>
        )}

        <Row className="vehicle-excel-workspace">
          <Col xl={5}>
            <Card className="excel-panel excel-import-panel h-100">
              <CardBody>
                <div className="excel-panel-heading">
                  <span className="excel-panel-icon excel-panel-icon-blue"><i className="ri-upload-cloud-2-line" /></span>
                  <div>
                    <span className="excel-step-label">Étape 01</span>
                    <h2>Importer un fichier</h2>
                    <p>Vérifiez les données avant de les intégrer à JA‑Eval.</p>
                  </div>
                </div>

                <label className={`excel-dropzone ${file ? "has-file" : ""}`} htmlFor="vehicleExcelFile">
                  <Input
                    id="vehicleExcelFile"
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={chooseFile}
                  />
                  {file ? (
                    <>
                      <span className="excel-file-icon"><i className="ri-file-excel-2-line" /></span>
                      <span className="excel-file-copy">
                        <strong>{file.name}</strong>
                        <small>{formatSize(file.size)} · Fichier prêt pour le contrôle</small>
                      </span>
                      <span className="excel-file-change">Changer</span>
                    </>
                  ) : (
                    <>
                      <span className="excel-upload-icon"><i className="ri-upload-cloud-line" /></span>
                      <strong>Choisir un fichier Excel</strong>
                      <small>Format .xlsx · 10 Mo maximum</small>
                      <span className="excel-browse-button">Parcourir les fichiers</span>
                    </>
                  )}
                </label>

                <div className="excel-import-help">
                  <span><i className="ri-checkbox-circle-line" /> En-têtes contrôlés</span>
                  <span><i className="ri-checkbox-circle-line" /> Doublons détectés</span>
                  <span><i className="ri-checkbox-circle-line" /> Erreurs détaillées</span>
                </div>

                <div className="excel-panel-actions">
                  <Button color="primary" onClick={runPreview} disabled={!file || Boolean(busy)}>
                    {busy === "preview" ? <Spinner size="sm" /> : <i className="ri-search-eye-line" />}
                    Vérifier le fichier
                  </Button>
                  <Button color="success" onClick={confirmImport} disabled={!preview || validRows === 0 || Boolean(busy)}>
                    {busy === "import" ? <Spinner size="sm" /> : <i className="ri-check-double-line" />}
                    Valider l’import
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={7}>
            <Card className="excel-panel excel-export-panel h-100">
              <CardBody>
                <div className="excel-panel-heading">
                  <span className="excel-panel-icon excel-panel-icon-green"><i className="ri-download-cloud-2-line" /></span>
                  <div>
                    <span className="excel-step-label">Étape 02</span>
                    <h2>Exporter les véhicules</h2>
                    <p>Affinez le périmètre ou exportez l’ensemble du référentiel.</p>
                  </div>
                  {activeFilters > 0 && <Badge pill color="primary">{activeFilters} filtre{activeFilters > 1 ? "s" : ""}</Badge>}
                </div>

                <div className="excel-filter-section">
                  <span className="excel-filter-title"><i className="ri-calendar-line" /> Période et décision</span>
                  <Row>
                    <Col md={4}><FormGroup><Label>Du</Label><Input type="date" name="dateFrom" value={filters.dateFrom} onChange={setFilter} /></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>Au</Label><Input type="date" name="dateTo" value={filters.dateTo} onChange={setFilter} /></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>Décision</Label><Input type="select" name="decision" value={filters.decision} onChange={setFilter}><option value="">Toutes</option><option>En attente</option><option>Garder</option><option>Céder</option></Input></FormGroup></Col>
                  </Row>
                </div>

                <div className="excel-filter-section">
                  <span className="excel-filter-title"><i className="ri-filter-3-line" /> Critères du dossier</span>
                  <Row>
                    <Col md={4}><FormGroup><Label>Expert</Label><Input name="expert" placeholder="Nom de l’expert" value={filters.expert} onChange={setFilter} /></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>Lieu de dépôt</Label><Input name="lieuDepot" placeholder="Ville ou dépôt" value={filters.lieuDepot} onChange={setFilter} /></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>Type de réforme</Label><Input type="select" name="typeReforme" value={filters.typeReforme} onChange={setFilter}><option value="">Tous</option><option>Réforme technique</option><option>Réforme économique</option></Input></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>Immatriculation</Label><Input name="immatriculation" placeholder="Ex. 12345-A-6" value={filters.immatriculation} onChange={setFilter} /></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>Référence sinistre</Label><Input name="referenceSinistre" placeholder="Référence" value={filters.referenceSinistre} onChange={setFilter} /></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>N° appel d’offres</Label><Input name="numeroAppelOffres" placeholder="Numéro" value={filters.numeroAppelOffres} onChange={setFilter} /></FormGroup></Col>
                  </Row>
                </div>

                <div className="excel-panel-actions excel-export-actions">
                  <Button color="link" onClick={() => setFilters(emptyFilters)} disabled={!activeFilters}>
                    <i className="ri-refresh-line" /> Réinitialiser
                  </Button>
                  <Button color="success" onClick={runExport} disabled={Boolean(busy)}>
                    {busy === "export" ? <Spinner size="sm" /> : <i className="ri-file-download-line" />}
                    Exporter en Excel
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {preview && (
          <Card className="excel-panel excel-results-panel">
            <CardBody>
              <div className="excel-results-head">
                <div className="excel-panel-heading mb-0">
                  <span className="excel-panel-icon excel-panel-icon-violet"><i className="ri-scan-2-line" /></span>
                  <div>
                    <span className="excel-step-label">Résultat du contrôle</span>
                    <h2>Aperçu de l’import</h2>
                    <p>Consultez les lignes avant de confirmer leur intégration.</p>
                  </div>
                </div>
                <div className="excel-results-stats">
                  <StatusPill tone="blue" icon="ri-file-list-3-line">{preview.total} lignes</StatusPill>
                  <StatusPill tone="green" icon="ri-checkbox-circle-line">{validRows} valides</StatusPill>
                  <StatusPill tone={rejected ? "red" : "neutral"} icon="ri-error-warning-line">{rejected} rejetées</StatusPill>
                </div>
              </div>
              {rejected > 0 && (
                <div className="excel-inline-warning">
                  <i className="ri-error-warning-line" />
                  <div><strong>Des corrections sont nécessaires</strong><span>Les lignes signalées seront ignorées lors de l’import.</span></div>
                </div>
              )}
              <div className="excel-table-wrap">
                <Table hover responsive className="excel-data-table align-middle mb-0">
                  <thead><tr><th>Ligne</th><th>Appel d’offres</th><th>Immatriculation</th><th>Réf. sinistre</th><th>Marque</th><th>Décision</th><th>Contrôle</th></tr></thead>
                  <tbody>
                    {(preview.rows || []).map((row) => (
                      <tr key={row.rowNumber} className={row.errors?.length ? "excel-row-error" : ""}>
                        <td><span className="excel-row-number">{row.rowNumber}</span></td>
                        <td>{row.numeroAppelOffres || "—"}</td>
                        <td><strong>{row.immatriculation || "—"}</strong></td>
                        <td>{row.referenceSinistre || "—"}</td>
                        <td>{row.marque || "—"}</td>
                        <td><StatusPill tone="neutral">{row.decision || "En attente"}</StatusPill></td>
                        <td>{row.errors?.length
                          ? <div className="excel-errors">{row.errors.map((error) => <span key={error}><i className="ri-close-circle-line" />{error}</span>)}</div>
                          : <StatusPill tone="green" icon="ri-check-line">Valide</StatusPill>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

        <Card className="excel-panel excel-history-panel">
          <CardBody>
            <div className="excel-results-head">
              <div className="excel-panel-heading mb-0">
                <span className="excel-panel-icon excel-panel-icon-amber"><i className="ri-history-line" /></span>
                <div>
                  <span className="excel-step-label">Traçabilité</span>
                  <h2>Historique des traitements</h2>
                  <p>Les 20 derniers imports réalisés sur la plateforme.</p>
                </div>
              </div>
              <Button color="light" className="excel-refresh-button" onClick={loadHistory}>
                <i className="ri-refresh-line" /> Actualiser
              </Button>
            </div>
            <div className="excel-table-wrap">
              <Table hover responsive className="excel-data-table align-middle mb-0">
                <thead><tr><th>Date</th><th>Fichier</th><th>Total</th><th>Créés</th><th>Mis à jour</th><th>Rejetés</th><th>Statut</th></tr></thead>
                <tbody>
                  {history.map((item) => (
                    <tr key={item.id}>
                      <td><span className="excel-history-date"><i className="ri-calendar-event-line" />{item.createdDate ? new Date(item.createdDate).toLocaleString("fr-FR") : "—"}</span></td>
                      <td><span className="excel-history-file"><i className="ri-file-excel-2-line" /><strong>{item.fileName}</strong></span></td>
                      <td>{item.totalRows}</td>
                      <td><span className="excel-count excel-count-green">{item.createdRows}</span></td>
                      <td><span className="excel-count excel-count-blue">{item.updatedRows}</span></td>
                      <td><span className={`excel-count ${item.rejectedRows ? "excel-count-red" : ""}`}>{item.rejectedRows}</span></td>
                      <td><StatusPill tone={item.rejectedRows ? "amber" : "green"} icon={item.rejectedRows ? "ri-alert-line" : "ri-check-line"}>{item.rejectedRows ? "Avec erreurs" : "Terminé"}</StatusPill></td>
                    </tr>
                  ))}
                  {!history.length && (
                    <tr><td colSpan="7"><div className="excel-empty-state"><i className="ri-inbox-archive-line" /><strong>Aucun import enregistré</strong><span>Vos prochains traitements apparaîtront ici.</span></div></td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}

export default VehicleExcelPage;
